import { StackedList } from "@/components/common/StackedList/StackedList"
import { Typography } from "@/components/common/Typography/Typography"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  answerText,
  getPortalDetail,
  getPortalSessions,
  PortalSession,
} from "@/services/api/extensionHistoryService"
import { IconWorld } from "@tabler/icons-react"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { useState } from "react"

function Session({ item: session }: { item: PortalSession }) {
  const { translate: t } = useTranslation()
  const [open, setOpen] = useState(false)
  const detail = useQuery({
    queryKey: ["extension-history", session.id, session.updatedAt],
    queryFn: () => getPortalDetail(session.id),
    enabled: open,
  })
  return (
    <article className="text-white">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-S px-S py-M text-left transition-colors hover:bg-yellow1/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow1"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <div className="flex min-w-0 items-center gap-S">
          <IconWorld
            size={24}
            stroke={1}
            className="shrink-0"
            aria-hidden="true"
          />
          <div className="space-y-1 min-w-0">
            <Typography variant="textS" className="truncate">
              {session.title}
            </Typography>
            <Typography variant="textXS" className="text-grey5">
              {t("EXT_HISTORY/SOURCE")} · {session.answerCount}{" "}
              {t(
                session.answerCount === 1
                  ? "EXT_HISTORY/ANSWER"
                  : "EXT_HISTORY/ANSWERS",
              )}
            </Typography>
          </div>
        </div>
        <Typography variant="textS" className="shrink-0 text-grey5">
          {new Date(session.updatedAt).toLocaleString()}
        </Typography>
      </button>
      {open && (
        <div className="space-y-4 border-t border-grey4 bg-grey2 px-S py-M">
          {session.pageUrl && (
            <a
              href={session.pageUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm break-all text-yellow1 hover:underline"
            >
              {session.pageUrl}
            </a>
          )}
          {detail.isPending && (
            <p role="status" className="text-grey5">
              {t("EXT_HISTORY/LOADING")}
            </p>
          )}
          {detail.isError && (
            <button
              type="button"
              className="text-yellow1 hover:underline"
              onClick={() => detail.refetch()}
            >
              {t("EXT_HISTORY/RETRY")}
            </button>
          )}
          {detail.data?.answers.map((answer) => (
            <section
              key={answer.id}
              className="space-y-2 border-b border-grey4 pb-M last:border-b-0"
            >
              <Typography as="h3" variant="textS-bold">
                {answer.question}
              </Typography>
              <p className="text-sm text-grey5">{t("EXT_HISTORY/GENERATED")}</p>
              <p className="whitespace-pre-wrap">
                {answerText(answer.generated) || "—"}
              </p>
              {!!answer.generated.citations?.length && (
                <p className="text-sm text-grey5">
                  {answer.generated.citations
                    .map((c) =>
                      c.page != null && c.page > 0
                        ? `${c.file} (${c.page})`
                        : c.file,
                    )
                    .join(", ")}
                </p>
              )}
              {answer.submitted ? (
                <>
                  <p className="text-sm text-grey5">
                    {t("EXT_HISTORY/SUBMITTED")} ·{" "}
                    {answer.submittedAt &&
                      new Date(answer.submittedAt).toLocaleString()}
                  </p>
                  <p className="whitespace-pre-wrap">
                    {answerText(answer.submitted) || "—"}
                  </p>
                </>
              ) : (
                <p className="text-sm text-grey5">{t("EXT_HISTORY/DRAFT")}</p>
              )}
            </section>
          ))}
          <p className="text-xs text-grey5">{t("EXT_HISTORY/NOTE")}</p>
        </div>
      )}
    </article>
  )
}

function Sessions({ search }: { search: string }) {
  const { translate: t } = useTranslation()
  const query = useInfiniteQuery({
    queryKey: ["extension-history", "list", search],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      getPortalSessions(search.slice(0, 300), pageParam),
    getNextPageParam: (page, pages) =>
      page.length === 50 ? pages.length * 50 : undefined,
  })
  return (
    <section aria-label={t("EXT_HISTORY/TITLE")} className="space-y-3">
      <h2 className="font-semibold">{t("EXT_HISTORY/TITLE")}</h2>
      {query.isPending && <p role="status">{t("EXT_HISTORY/LOADING")}</p>}
      {query.isError && (
        <button type="button" onClick={() => query.refetch()}>
          {t("EXT_HISTORY/RETRY")}
        </button>
      )}
      {query.data && (
        <StackedList
          items={query.data.pages.flat()}
          ItemComponent={Session}
          getItemKey={(session) => session.id}
        />
      )}
      {query.data?.pages[0]?.length === 0 && (
        <p className="text-gray-500 text-sm">{t("EXT_HISTORY/EMPTY")}</p>
      )}
      {query.hasNextPage && (
        <button
          type="button"
          disabled={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          {t("EXT_HISTORY/MORE")}
        </button>
      )}
    </section>
  )
}

export default function ExtensionHistory({ search }: { search: string }) {
  const enabled = useFeatureFlag("EXTENSION_HISTORY")
  const extensionEnabled = useFeatureFlag("EXTENSION_ENABLED")
  return enabled && extensionEnabled ? <Sessions search={search} /> : null
}
